import React from 'react';
import { Gift } from './StreamGifts';
import { Mic, MicOff, X, MessageSquare, LogOut, Users, Eye } from 'lucide-react';
import { SafeImage } from './SafeImage';
import { getImageUrl } from '../services/api';

// --- Interfaces ---

export interface Participant {
    id: string;
    username: string;
    avatar: string;
    profileImage?: string;
    isHost: boolean;
    isSpeaking: boolean;
    isMuted: boolean;
    isVip?: boolean; // Added VIP status
    volume: number;
    gifts?: any[];
}

export interface VoiceRoom {
    id: string;
    title: string;
    host: string;
    hostAvatar: string;
    participants: Participant[]; // These are SPEAKERS
    speakerRequests?: Participant[]; // Viewers requesting to speak
    audienceCount: number; // Count of listeners
    maxParticipants: number; // Max SPEAKERS
    isLive: boolean;
    tags: string[];
}

export interface ChatMessage {
    id: string;
    userId: string;
    username: string;
    avatar: string;
    message: string;
    timestamp: Date;
    isGift?: boolean;
    gift?: Gift & { sender?: string; recipient?: string; timestamp?: Date };
}

// --- Components ---

interface VoiceRoomHeaderProps {
    room: VoiceRoom;
    onLeave: () => void;
}

export const VoiceRoomHeader: React.FC<VoiceRoomHeaderProps> = ({ room, onLeave }) => {
    return (
        <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '16px 20px',
            background: 'rgba(20, 20, 20, 0.65)',
            backdropFilter: 'blur(16px)',
            borderBottom: '1px solid rgba(255,255,255,0.08)',
            zIndex: 10,
            boxShadow: '0 4px 30px rgba(0, 0, 0, 0.1)'
        }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                <div style={{
                    position: 'relative',
                    width: '38px',
                    height: '38px',
                }}>
                    <div style={{
                        position: 'absolute',
                        inset: -2,
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #ff007f, #7928ca)',
                        opacity: 0.8,
                        filter: 'blur(4px)'
                    }} />
                    <div style={{
                        position: 'relative',
                        width: '100%',
                        height: '100%',
                        borderRadius: '50%',
                        background: '#1a1a1a',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '18px',
                        border: '1px solid rgba(255,255,255,0.1)'
                    }}>
                        <Mic size={18} color="#fff" />
                    </div>
                </div>

                <div>
                    <div style={{
                        fontSize: '15px',
                        fontWeight: '700',
                        color: '#fff',
                        marginBottom: '4px',
                        letterSpacing: '0.3px',
                        textShadow: '0 2px 4px rgba(0,0,0,0.5)'
                    }}>
                        {room.title}
                    </div>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px'
                    }}>
                        {/* Live Badge */}
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            background: 'rgba(255, 0, 127, 0.15)',
                            padding: '2px 8px',
                            borderRadius: '12px',
                            border: '1px solid rgba(255, 0, 127, 0.3)'
                        }}>
                            <div style={{
                                width: '6px',
                                height: '6px',
                                borderRadius: '50%',
                                backgroundColor: '#ff4081',
                                boxShadow: '0 0 8px #ff4081'
                            }} />
                            <span style={{
                                color: '#ff4081',
                                fontSize: '10px',
                                fontWeight: '700',
                                letterSpacing: '0.5px'
                            }}>
                                LIVE
                            </span>
                        </div>

                        {/* Speaker Count */}
                        <span style={{
                            color: 'rgba(255,255,255,0.5)',
                            fontSize: '11px',
                            fontWeight: '500',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                        }}>
                            <Users size={12} /> {room.participants.length}/{room.maxParticipants}
                        </span>

                        {/* Audience Count */}
                        <span style={{
                            color: 'rgba(255,255,255,0.5)',
                            fontSize: '11px',
                            fontWeight: '500',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                        }}>
                            <Eye size={12} /> {room.audienceCount}
                        </span>
                    </div>
                </div>
            </div>

            <button
                onClick={onLeave}
                style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.08)',
                    color: '#fff',
                    border: '1px solid rgba(255,255,255,0.1)',
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    fontSize: '12px'
                }}
            >
                <X size={16} />
            </button>
        </div>
    );
};

interface ParticipantGridProps {
    room: VoiceRoom;
    isInRoom: boolean;
    isSpeaker?: boolean;
    onJoin: () => void;
    onInvite: () => void;
    selectedParticipantId?: string | null;
    onSelectParticipant?: (id: string) => void;
}

export const ParticipantGrid: React.FC<ParticipantGridProps> = ({
    room,
    isInRoom,
    isSpeaker,
    onJoin,
    onInvite,
    selectedParticipantId,
    onSelectParticipant
}) => {
    return (
        <div style={{
            flex: 1,
            padding: '24px 20px',
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: '20px'
        }}>

            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: '12px',
                paddingBottom: '20px' // Padding needed only for scroll clearance
            }}>
                {room.participants.map(participant => {
                    const isSelected = selectedParticipantId === participant.id;

                    return (
                        <div
                            key={participant.id}
                            onClick={() => onSelectParticipant && onSelectParticipant(participant.id)}
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                position: 'relative',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                transform: isSelected ? 'scale(1.05)' : 'scale(1)',
                            }}
                        >
                            {/* Avatar Container */}
                            <div style={{
                                position: 'relative',
                                width: '72px',
                                height: '72px',
                                marginBottom: '6px'
                            }}>
                                {/* Selection Indicator */}
                                {isSelected && (
                                    <div style={{
                                        position: 'absolute',
                                        inset: -4,
                                        borderRadius: '50%',
                                        border: '2px solid #FFD700',
                                        boxShadow: '0 0 15px rgba(255, 215, 0, 0.6)',
                                        animation: 'pulse 2s infinite'
                                    }} />
                                )}

                                {/* VIP Glow Effect */}
                                {participant.isVip && (
                                    <div style={{
                                        position: 'absolute',
                                        inset: -3,
                                        borderRadius: '50%',
                                        background: 'conic-gradient(from 0deg, #FFD700, #FDB931, #FFD700)',
                                        animation: 'spin 4s linear infinite',
                                        opacity: 0.7,
                                        zIndex: 0
                                    }} />
                                )}

                                {/* Speaking Ring Animation */}
                                {participant.isSpeaking && !participant.isMuted && (
                                    <div style={{
                                        position: 'absolute',
                                        inset: -4,
                                        borderRadius: '50%',
                                        border: '2px solid #ff007f',
                                        boxShadow: '0 0 15px rgba(255, 0, 127, 0.4)',
                                        opacity: 0.8,
                                        animation: 'pulse 1.5s infinite',
                                        zIndex: 1
                                    }} />
                                )}

                                <div style={{
                                    width: '100%',
                                    height: '100%',
                                    borderRadius: '50%',
                                    overflow: 'hidden',
                                    border: isSelected
                                        ? '2px solid #FFD700'
                                        : (participant.isSpeaking ? '2px solid #ff007f' : (participant.isVip ? '2px solid #FFD700' : '2px solid rgba(255,255,255,0.1)')),
                                    background: 'rgba(0,0,0,0.3)',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                                    position: 'relative',
                                    zIndex: 2
                                }}>
                                    <SafeImage
                                        src={participant.profileImage ? getImageUrl(participant.profileImage) : undefined}
                                        fallbackSrc={`https://ui-avatars.com/api/?name=${participant.username}&background=random`}
                                        alt={participant.username}
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover',
                                        }}
                                    />
                                </div>

                                {/* Host Icon */}
                                {participant.isHost && (
                                    <div style={{
                                        position: 'absolute',
                                        bottom: '0',
                                        right: '0',
                                        background: '#FFD700',
                                        borderRadius: '50%',
                                        width: '20px',
                                        height: '20px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        border: '2px solid #1a1a1a',
                                        fontSize: '10px',
                                        zIndex: 5
                                    }}>
                                        🎤
                                    </div>
                                )}

                                {/* VIP Crown Icon (if not host or distinct) */}
                                {participant.isVip && !participant.isHost && (
                                    <div style={{
                                        position: 'absolute',
                                        bottom: '0',
                                        right: '0',
                                        background: 'linear-gradient(135deg, #FFD700 0%, #FFA000 100%)',
                                        borderRadius: '50%',
                                        width: '20px',
                                        height: '20px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        border: '2px solid #1a1a1a',
                                        fontSize: '10px',
                                        zIndex: 5,
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.3)'
                                    }}>
                                        👑
                                    </div>
                                )}

                                {participant.isMuted && (
                                    <div style={{
                                        position: 'absolute',
                                        bottom: '0',
                                        right: (participant.isHost || participant.isVip) ? '18px' : '0',
                                        background: '#ff4444',
                                        borderRadius: '50%',
                                        width: '20px',
                                        height: '20px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        border: '2px solid #1a1a1a',
                                        zIndex: 4,
                                        color: 'white'
                                    }}>
                                        <span style={{ fontSize: '10px' }}>🔇</span>
                                    </div>
                                )}
                            </div>

                            {/* Username with VIP styling */}
                            <div style={{
                                fontSize: '12px',
                                fontWeight: (isSelected || participant.isVip) ? '700' : '500',
                                color: isSelected ? '#FFD700' : (participant.isVip ? '#FDB931' : 'rgba(255,255,255,0.9)'),
                                textAlign: 'center',
                                maxWidth: '100%',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                textShadow: participant.isVip ? '0 0 10px rgba(253, 185, 49, 0.4)' : '0 1px 3px rgba(0,0,0,0.8)',
                            }}>
                                {participant.username}
                            </div>
                        </div>
                    );
                })}

                {/* Render Empty Slots to fill up to maxParticipants */}
                {Array.from({ length: Math.max(0, room.maxParticipants - room.participants.length) }).map((_, index) => (
                    <div
                        key={`empty-${index}`}
                        onClick={() => {
                            if (!isSpeaker) onJoin();
                            else onInvite();
                        }}
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            opacity: 0.8,
                            paddingTop: '6px' // Visual alignment with avatars
                        }}
                    >
                        <div style={{
                            width: '60px',
                            height: '60px',
                            borderRadius: '50%',
                            border: '1px dashed rgba(255,255,255,0.4)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginBottom: '6px',
                            background: 'rgba(255,255,255,0.05)',
                            transition: 'all 0.2s'
                        }}>
                            <span style={{ fontSize: '24px', color: 'rgba(255,255,255,0.6)' }}>+</span>
                        </div>
                        <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.6)', fontWeight: '500' }}>
                            {!isSpeaker ? 'Katıl' : 'Davet Et'}
                        </div>
                    </div>
                ))}
            </div>

            {/* Styles for animation */}
            <style>{`
                @keyframes pulse {
                    0% { transform: scale(1); opacity: 0.6; }
                    50% { transform: scale(1.1); opacity: 0; }
                    100% { transform: scale(1); opacity: 0; }
                }
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
            `}</style>
        </div>
    );
};

interface VoiceControlsProps {
    isMuted: boolean;
    toggleMute: () => void;
    isChatVisible: boolean;
    toggleChat: () => void;
    onGift: () => void;
    onLeave: () => void;
    isSpeaker?: boolean;
    onToggleRole?: () => void;
}

export const VoiceControls: React.FC<VoiceControlsProps> = ({
    isMuted,
    toggleMute,
    isChatVisible,
    toggleChat,
    onGift,
    onLeave,
    isSpeaker,
    onToggleRole
}) => {
    return (
        <div style={{
            width: '100%',
            height: '85px', // Standard mobile tab bar height
            background: 'rgba(10, 10, 10, 0.95)',
            backdropFilter: 'blur(20px)',
            borderTop: '1px solid rgba(255, 255, 255, 0.08)',
            zIndex: 20,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-around',
            paddingBottom: '15px', // Safe area padding
            boxShadow: '0 -10px 40px rgba(0,0,0,0.5)',
            flexShrink: 0, // Prevent shrinking
        }}>
            {/* Role Toggle (Sit/Stand) */}
            {onToggleRole && (
                <ControlButton
                    onClick={onToggleRole}
                    active={!!isSpeaker}
                    activeColor="#4caf50"
                    icon={isSpeaker ? <Mic size={20} /> : <Users size={20} />} // Sofa to relax/leave, Seat to sit
                    size="44px"
                // Label could be helpful but keeping it icon-only for now consistent with design
                />
            )}

            {/* Mic Toggle (Only if speaker) */}
            {isSpeaker && (
                <ControlButton
                    onClick={toggleMute}
                    active={isMuted}
                    activeColor="rgba(239, 83, 80, 0.4)"
                    icon={isMuted ? <MicOff size={20} color="#ff4444" /> : <Mic size={20} />}
                    size="44px"
                />
            )}

            {/* Chat Toggle */}
            <ControlButton
                onClick={toggleChat}
                active={isChatVisible}
                activeColor="#ff007f"
                icon={<MessageSquare size={20} />}
                size="44px"
            />

            {/* Gift Button - Featured */}
            <button
                onClick={onGift}
                style={{
                    width: '64px', // Bigger gift button
                    height: '64px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #FFD700 0%, #FFA000 100%)',
                    border: '4px solid rgba(25, 25, 25, 1)',
                    fontSize: '28px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    transform: 'translateY(-20px)',
                    boxShadow: '0 8px 20px rgba(255, 160, 0, 0.4)',
                    transition: 'transform 0.2s',
                    zIndex: 25
                }}
            >
                🎁
            </button>

            {/* Leave */}
            <ControlButton
                onClick={onLeave}
                active={false}
                customStyle={{ color: '#ef5350', background: 'rgba(239, 83, 80, 0.15)' }}
                icon={<LogOut size={22} />}
                size="44px"
            />
        </div>
    );
};

const ControlButton = ({ onClick, active, activeColor, icon, customStyle, size = '48px' }: any) => (
    <button
        onClick={onClick}
        style={{
            width: size,
            height: size,
            borderRadius: '20px', // More rounded

            backgroundColor: active && activeColor ? activeColor : 'rgba(255,255,255,0.05)',
            color: active && activeColor ? '#fff' : 'rgba(255,255,255,0.8)',
            border: '1px solid rgba(255,255,255,0.05)',
            fontSize: '18px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            transition: 'all 0.2s',
            ...customStyle
        }}
    >
        {icon}
    </button>
);

interface ChatOverlayProps {
    messages: ChatMessage[];
    isVisible: boolean;
    newMessage: string;
    onNewMessageChange: (text: string) => void;
    onSendMessage: () => void;
}

export const ChatOverlay: React.FC<ChatOverlayProps> = ({
    messages,
    isVisible,
    newMessage,
    onNewMessageChange,
    onSendMessage
}) => {
    if (!isVisible) return null;

    return (
        <div style={{
            position: 'absolute',
            bottom: '10px',
            left: '12px',
            right: '12px',
            maxHeight: '40%',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            pointerEvents: 'none',
            zIndex: 15,
        }}>

            {/* Messages Area */}
            <div style={{
                flex: 1,
                overflowY: 'auto',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                maskImage: 'linear-gradient(to top, black 90%, transparent 100%)',
                WebkitMaskImage: 'linear-gradient(to top, black 90%, transparent 100%)',
                paddingRight: '4px',
                pointerEvents: 'auto'
            }} className="hide-scrollbar">
                {messages.slice(-8).map((message) => (
                    <div key={message.id} style={message.isGift ? {
                        alignSelf: 'flex-start',
                        background: 'linear-gradient(135deg, rgba(255,42,133,0.9), rgba(233,64,87,0.9))',
                        backdropFilter: 'blur(10px)',
                        padding: '8px 14px',
                        borderRadius: '18px',
                        borderBottomLeftRadius: '4px',
                        border: '1px solid rgba(255,255,255,0.2)',
                        maxWidth: '85%',
                        boxShadow: '0 4px 15px rgba(255,42,133,0.4)',
                        animation: 'fadeIn 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                    } : {
                        alignSelf: 'flex-start',
                        backgroundColor: 'rgba(20, 20, 20, 0.75)',
                        backdropFilter: 'blur(12px)',
                        padding: '8px 14px',
                        borderRadius: '18px',
                        borderBottomLeftRadius: '4px',
                        border: '1px solid rgba(255, 255, 255, 0.08)',
                        maxWidth: '85%',
                        boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                        animation: 'fadeIn 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '2px' }}>
                            <span style={{
                                fontSize: '11px',
                                fontWeight: '700',
                                color: message.isGift ? '#FFF0F5' : (message.userId === 'current-user' ? '#69f0ae' : 'rgba(255,255,255,0.9)'),
                                letterSpacing: '0.3px',
                                textShadow: message.isGift ? '0 1px 2px rgba(0,0,0,0.8)' : 'none'
                            }}>
                                {message.username}
                            </span>
                        </div>
                        <div style={{
                            fontSize: '13px',
                            color: message.isGift ? '#fff' : 'rgba(255, 255, 255, 0.8)',
                            lineHeight: '1.4',
                            fontWeight: message.isGift ? 'bold' : '400',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                        }}>
                            {message.isGift && message.gift ? (
                                <>sana <span style={{ fontSize: '20px' }}>{message.gift.icon}</span> {message.gift.name} gönderdi!</>
                            ) : (
                                message.message
                            )}
                        </div>
                    </div>
                ))}
            </div>

            {/* Input Area */}
            <div style={{
                pointerEvents: 'auto',
                display: 'flex',
                gap: '10px',
                alignItems: 'center',
                background: 'rgba(0,0,0,0.4)',
                backdropFilter: 'blur(20px)',
                padding: '8px',
                borderRadius: '24px',
                border: '1px solid rgba(255,255,255,0.08)'
            }}>
                <input
                    type="text"
                    value={newMessage}
                    onChange={(e) => onNewMessageChange(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && onSendMessage()}
                    placeholder="Sohbete katıl..."
                    style={{
                        flex: 1,
                        backgroundColor: 'transparent',
                        border: 'none',
                        padding: '8px 12px',
                        color: '#fff',
                        fontSize: '14px',
                        outline: 'none',
                        fontWeight: '500'
                    }}
                />
                <button
                    onClick={onSendMessage}
                    disabled={!newMessage.trim()}
                    style={{
                        backgroundColor: newMessage.trim() ? '#ff007f' : 'rgba(255,255,255,0.1)',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '50%',
                        width: '36px',
                        height: '36px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: newMessage.trim() ? 'pointer' : 'default',
                        transition: 'all 0.2s',
                        opacity: newMessage.trim() ? 1 : 0.5
                    }}
                >
                    ➤
                </button>
            </div>
        </div>
    );
};

